import { reportsApi } from './api/reportsApi';

import { StratusStatsReport } from './components/stratus-stats-report/stratus-stats-report';
import { ReportModel } from './models/report/ReportModel';
import { TemplateModel } from './models/template/TemplateModel';
import ReportType from './enums/report-type';
import { matchesApi } from './api/matchesApi';
import { MatchTeamModel } from './models/scoresummary/MatchTeamModel';
import { TeamModel } from './models/report/TeamModel';
import { MetricModel } from './models/report/MetricModel';
import ScoreTypes, { ScoreTypeMapping } from './enums/score-types';
import { MatchModel } from './models/report/MatchModel';
import { MatchMetaDataModel } from './models/report/meta-data/MatchMetaDataModel';
import { reportResultsApi } from './api/reportResultsApi';
import { RowModel } from './models/template/RowModel';
import CardComponentType from './enums/card-component-type';
import { teamsApi } from './api/teamsApi';
import ReportSetup from './components/report-setup/report-setup';
import ReportBuilder from './components/report-builder/report-builder';
import Dropdown from "./components/dropdown/dropdown";

class StratusStatistics {
    private _reportsApi: reportsApi;
    private _matchesApi: matchesApi;
    private _teamsApi: teamsApi;
    private _reportResultsApi: reportResultsApi;
    private _playlistClickThrough: boolean;

    constructor() {

    }

    initBuildReport(reportContainer: HTMLElement, baseUrl: string, userId: string) {
        reportContainer.appendChild(new ReportBuilder(reportContainer, baseUrl, userId));
    }

    async initSetupReport(setupContainer: HTMLElement, reportContainer: HTMLElement, mediaUrl: string, portalUrl: string, userId: string) {
        setupContainer.appendChild(new ReportSetup(reportContainer, mediaUrl, portalUrl, userId, this.initReport.bind(this.initReport)));
    }

    async initReport(baseUrl: string,
        userId: string,
        container: HTMLElement,
        reportId: string,
        matchId: string,
        templateId: string,
        loader: any,
        extraStyles: string,
        reportType: number,
        logoTop: boolean,
        showTeams: boolean,
        seasonId: string,
        showPlayerStats: boolean,
        playerTemplateId: string,
        playlistClickThrough: boolean,
        seriesId: string,
        reportFilterId: string) {

        this._reportsApi = new reportsApi(baseUrl, userId);
        this._matchesApi = new matchesApi(baseUrl, userId);
        this._teamsApi = new teamsApi(baseUrl, userId);
        this._reportResultsApi = new reportResultsApi(baseUrl, userId);

        this._playlistClickThrough = playlistClickThrough;

        if (loader)
            container.appendChild(loader);
        else
            container.innerHTML = "<div>fetching stats</div>";

        let report: ReportModel = null;
        let template: TemplateModel = null;
        let playerTemplate: TemplateModel = null;
        let playerReport: ReportModel = null;
        let teamsFormatch;

        //Local functions
        let templateHasPlayerMetricsFunc = function (rows: RowModel[]) {
            let hasPlayerMetrics = false;

            for (let [i, row] of Object.entries(rows)) {
                for (let [j, column] of Object.entries(row.columns)) {
                    if (column.components && column.components.length > 0) {
                        for (let [k, component] of Object.entries(column.components)) {
                            if (component.type == CardComponentType.PlayerBreakdownTable) {
                                hasPlayerMetrics = true;
                                break;
                            }
                        }
                    } else {
                        if (templateHasPlayerMetricsFunc(column.rows)) {
                            hasPlayerMetrics = true;
                            break;
                        }
                    }
                }

                if (hasPlayerMetrics)
                    break;
            }

            return hasPlayerMetrics;
        };

        switch (reportType) {
            case ReportType.TeamMatchReport:
                let apiCalls = [this._reportsApi.GetTeamMatchReport(reportId, matchId, "", reportFilterId),
                this._reportsApi.GetTemplateFromMedia(userId, templateId),
                (playerTemplateId ? this._reportsApi.GetTemplateFromMedia(userId, playerTemplateId) : Promise.resolve()),
                (playerTemplateId ? this._reportsApi.GetPlayerMatchReport(reportId, matchId, "", "", reportFilterId) : Promise.resolve())];

                if (showTeams)
                    apiCalls.push(this._teamsApi.GetTeamsForMatch(matchId));
                else
                    apiCalls.push(Promise.resolve());

                [report, template, playerTemplate, playerReport, teamsFormatch] = await Promise.all(apiCalls);

                if (templateHasPlayerMetricsFunc(template.rows)) {
                    let matchPlayerReport = await this._reportsApi.GetPlayerMatchReport(reportId, matchId, '', '', reportFilterId);
                    report.players = matchPlayerReport.players;
                }

                if (teamsFormatch) {
                    for (let [i, teamForMatch] of Object.entries(teamsFormatch)) {
                        let team = report.teams.find((team) => team.teamId == teamForMatch["teamGuid"]);
                        team.score = teamForMatch["matchTeam"]["score"];
                    }
                }

                break;
            case ReportType.PlayerMatchReport:
                [report, template] = await Promise.all([
                    this._reportsApi.GetPlayerMatchReport(reportId, matchId, "", "", reportFilterId),
                    this._reportsApi.GetTemplateFromMedia(userId, templateId)
                ]);

                if (showTeams) {
                    let teamsForMatch = await this._teamsApi.GetTeamsForMatch(matchId);

                    if (teamsForMatch) {
                        for (let [i, teamForMatch] of Object.entries(teamsForMatch)) {
                            let team = report.teams.find((team) => team.teamId == teamForMatch["teamGuid"]);
                            team.score = teamForMatch["matchTeam"]["score"];
                        }
                    }
                }

                break;
            case ReportType.TeamSeasonReport:
                [report, template] = await Promise.all([
                    this._reportsApi.GetTeamSeasonReport(reportId, seasonId, "", reportFilterId),
                    this._reportsApi.GetTemplateFromMedia(userId, templateId)
                ]);
                break;
            case ReportType.PlayerSeasonReport:
                [report, template] = await Promise.all([
                    this._reportsApi.GetPlayerSeasonReport(reportId, seasonId, "", "", reportFilterId),
                    this._reportsApi.GetTemplateFromMedia(userId, templateId)
                ]);
                break;

            case ReportType.TeamSeriesReport:
                [report, template] = await Promise.all([
                    this._reportsApi.GetTeamSeriesReport(reportId, seriesId, "", reportFilterId),
                    this._reportsApi.GetTemplateFromMedia(userId, templateId)
                ]);
                break;
            case ReportType.PlayerSeriesReport:
                [report, template] = await Promise.all([
                    this._reportsApi.GetPlayerSeriesReport(reportId, seriesId, "", "", reportFilterId),
                    this._reportsApi.GetTemplateFromMedia(userId, templateId)
                ]);
                break;
            case ReportType.TeamMatchScoreSummary:
                let matchTeams: MatchTeamModel[];

                [matchTeams, template] = await Promise.all([
                    this._matchesApi.GetScoreSummaryForMatch(matchId),
                    this._reportsApi.GetTemplateFromMedia(userId, templateId)
                ]);

                let awayTeam = matchTeams.find(matchTeam => matchTeam.isPrimary == false);
                let homeTeam = matchTeams.find(matchTeam => matchTeam.isPrimary == true);

                report = <ReportModel>{
                    teams: [],
                    matches: [<MatchModel>{
                        matchId: matchId,
                        matchMetaData: <MatchMetaDataModel>{
                            awayTeamID: awayTeam.teamGuid,
                            homeTeamID: homeTeam.teamGuid
                        }
                    }]
                };

                for (let [i, matchTeam] of Object.entries(matchTeams)) {
                    let teamModel: TeamModel = <TeamModel>{
                        teamId: matchTeam.teamGuid,
                        metrics: [],
                        teamMetaData: {}
                    };

                    for (let [j, scoreSummary] of Object.entries(matchTeam.matchScoreSummary)) {
                        let scoreTypeMapping: ScoreTypeMapping = ScoreTypes.getScoreType(scoreSummary.scoreTypeId);

                        teamModel.metrics.push(<MetricModel>{
                            metricId: scoreTypeMapping.metricId.toString(),
                            value: scoreSummary.count ?? 0,
                            itemId: teamModel.teamId
                        });
                    }

                    report.teams.push(teamModel);
                }
                break;
        }

        container.innerHTML = '';

        let playerStatsReport;
        if (playerReport && playerTemplateId) {
            playerStatsReport = new StratusStatsReport(matchId, playerTemplate, playerReport, extraStyles, logoTop, false, false, reportType, null, this._reportResultsApi, this._playlistClickThrough, userId, playerReport.reportId);
        }

        let statsReport = new StratusStatsReport(matchId, template, report, extraStyles, logoTop, showTeams, showPlayerStats, reportType, playerStatsReport, this._reportResultsApi, this._playlistClickThrough, userId, report.reportId);

        container.appendChild(statsReport);
    }
}

const stratusStatistics = new StratusStatistics();

export { stratusStatistics as instance }

window.customElements.define('index-stratus-stats-dropdown', Dropdown as any);